import React from 'react';
import { useFormik } from 'formik';
import { Box, Button, TextField, FormControlLabel, Switch } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import SelectField from 'components/SelectField';
import ConsumptionsReportFormProps from 'types/forms/ConsumptionsReportFormProps';
import { AdminForm, EditorForm } from './schemas/ConsumptionsReportForm';
import AppContext from 'contexts/AppContext';
import { getAllVenues } from 'services/venuesServices';
import { getAvailableCloverPoints } from 'services/cloverServices';
import Venue from 'types/models/Venue';
import Commerce from 'types/models/Commerce';
import UserRoleType from 'types/enums/UserRoleType';
import CloverPoint from 'types/models/CloverPoint';
import { ReducerState } from 'types/models/ReducerAction';
import { cloverPointsReducer as reducer, initialStateForCloverPoints } from 'utils/report';
import { parseDateToFormik, parseDateTimeLumen } from 'utils/datetime';
import LuxuryBox from 'types/models/LuxuryBox';
import 'styles/forms/ConsumptionsReportForm.scss';
import { getAllReportingOrders } from 'services/orderServices';

export default ({
  setCurrentFormData,
  isForBillingOrders = false,
  isForCashBoxes = false,
  justForDownload = false,
}: ConsumptionsReportFormProps): JSX.Element => {
  const { authData, isLoading, setIsLoading } = React.useContext(AppContext);
  const [venues, setVenues] = React.useState<Venue[] | null>(null);
  const [venueLuxuryBoxes, setVenueLuxuryBoxes] = React.useState<LuxuryBox[] | null>(null);
  const [venueCommerces, setVenueCommerces] = React.useState<Commerce[] | null>(null);
  const [state, dispatch] = React.useReducer(reducer, initialStateForCloverPoints);
  const [selectedVenueTimeZone, setSelectedVenueTimeZone] = React.useState<string | null>(null);
  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    const getVenues = async () => {
      const allVenues = await getAllVenues();
      setVenues(allVenues);
    };
    getVenues();
  }, []);

  const venuesForSelect = (venues: Venue[]) => {
    return venues.map((venue) => {
      return {
        value: venue.id,
        label: venue.name,
      };
    });
  };

  const commercesForSelect = (commerces: Commerce[]) => {
    if (authData?.user.role === UserRoleType.CommerceEditor) {
      const commerce = venueCommerces?.find((commerce) => commerce.editorId === authData.user.id);
      if (commerce) {
        return [{ value: commerce.id, label: commerce.name }];
      }
    }

    return commerces.map((commerce) => {
      return {
        value: commerce.id,
        label: commerce.name,
      };
    });
  };

  const luxuryBoxesForSelect = (luxuryBoxes: LuxuryBox[]) => {
    return luxuryBoxes.map((luxuryBox) => {
      return {
        value: luxuryBox.id,
        label: `${luxuryBox.floor}-${luxuryBox.identifierOnFloor}`,
      };
    });
  };

  const cloverPointsForSelect = (state: ReducerState[]) => {
    return state
      .reduce((acc, value) => {
        if (value.cloverPoints && value.cloverPoints.length > 0) {
          acc.push(value.cloverPoints);
        }
        return acc;
      }, [] as Array<CloverPoint[]>)
      .flatMap((cloverPoint) => cloverPoint)
      .map((cloverPoint) => {
        return {
          value: cloverPoint.id,
          label: `${cloverPoint.description} - Serial: ${cloverPoint.serial}`,
        };
      });
  };

  const formik = useFormik({
    initialValues: {
      venueId: '',
      luxuryBoxIds: [] as string[],
      commerceIds: [] as string[],
      identificationNumber: '',
      dateFrom: '',
      dateTo: '',
      onlyInvoicedOrders: false,
      cloverPointIds: [] as string[],
      isCashPayment: false,
      isCardPayment: false,
      isQrPayment: false,
      isFromCheckingAccount: false,
      isPreOrder: false,
      isForTransfer: false,
    },
    validationSchema:
      authData?.user?.role === UserRoleType.Admin || authData?.user?.role === UserRoleType.VenueEditor
        ? AdminForm(t)
        : EditorForm(t),
    onSubmit: (values) => {
      setCurrentFormData({
        ...values,
        dateFrom: parseDateTimeLumen(moment(values.dateFrom).toDate(), selectedVenueTimeZone ?? ''),
        dateTo: parseDateTimeLumen(moment(values.dateTo).toDate(), selectedVenueTimeZone ?? ''),
        localTimezoneIdentifier: selectedVenueTimeZone ?? '',
        commerceIds:
          values.commerceIds.length > 0
            ? values.commerceIds
            : commercesForSelect(venueCommerces ?? []).map((item) => item.value),
      });
      formik.setSubmitting(false);
    },
  });

  const settingPossibleCloverPoints = React.useCallback(
    async (venueId?: string, commerceIds?: string[]) => {
      if (venueId) {
        const isVenueIdInCloverPointsState = state.find(
          (cloverPoint) => cloverPoint.entityType === 'venue' && cloverPoint.entityId === formik.values.venueId,
        );
        if (!isVenueIdInCloverPointsState) {
          dispatch({ type: 'cleanup-clover-points', operation: 'reset' });
          const correspondingVenue = venues?.find((venue) => venue.id === formik.values.venueId);
          if (correspondingVenue) {
            const cloverConfigId = correspondingVenue.paymentConfig?.cloverPointConfigId;
            if (cloverConfigId) {
              const cloverPoints = await getAvailableCloverPoints(cloverConfigId);
              dispatch({
                type: 'new-venue-clover-points',
                operation: 'add',
                payload: {
                  entityType: 'venue',
                  cloverPoints,
                  entityId: correspondingVenue.id,
                },
              });
            }
          }
        }

        const currentCommerceIdsForCloverPoints = state.reduce((acc, point) => {
          if (point.entityType === 'commerce') {
            acc.push(point.entityId);
          }
          return acc;
        }, [] as string[]);

        if (commerceIds) {
          for await (const commerceId of commerceIds) {
            if (!currentCommerceIdsForCloverPoints.includes(commerceId)) {
              const correspondingCommerce = venueCommerces?.find((commerce) => commerce.id === commerceId);
              if (correspondingCommerce) {
                const cloverConfigId = correspondingCommerce.paymentConfig?.cloverPointConfigId;
                if (cloverConfigId) {
                  const cloverPoints = await getAvailableCloverPoints(cloverConfigId);
                  dispatch({
                    type: 'new-commerce-clover-points',
                    operation: 'add',
                    payload: {
                      entityType: 'commerce',
                      cloverPoints,
                      entityId: correspondingCommerce.id,
                    },
                  });
                }
              }
            }
          }
          if (state.length > 0 && state[0]?.entityId !== initialStateForCloverPoints[0]?.entityId)
            dispatch({
              type: 'delete-commerce-clover-points-not-present-in-id-list',
              operation: 'delete',
              selectedIds: [venueId, ...commerceIds],
            });
        }
      }
    },
    [formik.values.venueId, formik.values.commerceIds],
  );

  const downloadReport = async (): Promise<void> => {
    if (!formik.values) return;
    const body = {
      ...formik.values,
      dateFrom: parseDateTimeLumen(moment(formik.values.dateFrom).toDate(), selectedVenueTimeZone ?? ''),
      dateTo: parseDateTimeLumen(moment(formik.values.dateTo).toDate(), selectedVenueTimeZone ?? ''),
      localTimezoneIdentifier: selectedVenueTimeZone ?? '',
      commerceIds:
        formik.values.commerceIds.length > 0
          ? formik.values.commerceIds
          : commercesForSelect(venueCommerces ?? []).map((item) => item.value),
    };
    setIsLoading(true);
    try {
      const result = await getAllReportingOrders(body, i18n.language);
      if (result) {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(
          new Blob([result], {
            type: 'application/octet-stream',
          }),
        );
        a.download = 'reporte_ordenes.xlsx';
        a.click();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (formik.values.luxuryBoxIds.length > 0) {
      const selectedLuxuryBoxes = venueLuxuryBoxes?.filter((luxuryBox) => {
        const luxuryBoxOnSelectedIds = formik.values.luxuryBoxIds.find((value) => value === luxuryBox.id);
        return luxuryBoxOnSelectedIds;
      });
      const commerces = selectedLuxuryBoxes?.reduce((acc, value) => {
        if (value.commerces) {
          value.commerces.forEach((element) => {
            const commerceIsNotAlreadyOnAcc = acc.find((value) => value.id === element.id);
            if (!commerceIsNotAlreadyOnAcc) {
              acc.push(element);
            }
          });
        }
        return acc;
      }, [] as Commerce[]);
      if (commerces) setVenueCommerces(commerces);
    } else {
      const getSelectedVenue = (): Venue[] => {
        if (!venues || !formik.values.venueId) return [];
        return venues.filter((venue) => venue.id.includes(formik.values.venueId));
      };
      if (formik.values.cloverPointIds.length > 0) {
        formik.setFieldValue('cloverPointIds', []);
      }
      getSelectedVenue().map((element) => {
        if (!element.commerces) return [];
        setSelectedVenueTimeZone(element.timeZoneIdentifier);

        let commerces = element.commerces;
        // Si es de la pantalla de órdenes para facturar, filtramos los comercios que tienen configuración para facturar
        if (isForBillingOrders) {
          commerces = element.commerces.filter((commerce) => {
            if (!commerce.allowBilling) return false;
            if (commerce.billingSystem === 'arca') {
              return Boolean(commerce.afipBillingConfigId);
            }
            if (commerce.billingSystem === 'globalsis') {
              return Boolean(commerce.globalsisBillingConfigId);
            }
            return Boolean(commerce.globalsisBillingConfigId) || Boolean(commerce.afipBillingConfigId);
          });
        }
        return setVenueCommerces(commerces);
      });
    }
  }, [formik.values.venueId, formik.values.luxuryBoxIds.length]);

  React.useEffect(() => {
    settingPossibleCloverPoints(formik.values.venueId, formik.values.commerceIds);
    const possibleLuxuryBoxesByVenue = venues?.find(
      (value) => value.id === formik.values.venueId && value.luxuryBoxes !== undefined,
    )?.luxuryBoxes;
    if (possibleLuxuryBoxesByVenue) {
      setVenueLuxuryBoxes(possibleLuxuryBoxesByVenue);
    } else {
      if (venueLuxuryBoxes) {
        setVenueLuxuryBoxes(null);
      }
    }
  }, [formik.values.venueId]);

  React.useEffect(() => {
    if (formik.values.commerceIds.length > 0) {
      settingPossibleCloverPoints(formik.values.venueId, formik.values.commerceIds);
    }
  }, [formik.values.commerceIds]);

  React.useEffect(() => {
    if (formik.values.commerceIds.length > 0) {
      formik.setFieldValue('commerceIds', []);
    }
  }, [formik.values.venueId]);

  return (
    <form onSubmit={formik.handleSubmit}>
      {venues && (
        <SelectField
          label={t('consumptionsReport-form-venueId-label')}
          name="venueId"
          placeholder={t('consumptionsReport-form-venueId-placeholder')}
          value={formik.values.venueId}
          options={venuesForSelect(venues)}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.venueId}
          error={formik.errors.venueId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
      )}
      {venueLuxuryBoxes && venueLuxuryBoxes.length > 0 && (
        <SelectField
          label={t('consumptionsReport-form-luxuryBoxIds-label')}
          name="luxuryBoxIds"
          placeholder={t('consumptionsReport-form-luxuryBoxIds-placeholder')}
          value={formik.values.luxuryBoxIds}
          options={luxuryBoxesForSelect(venueLuxuryBoxes)}
          isMulti={true}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.luxuryBoxIds}
          error={formik.errors.luxuryBoxIds}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
      )}
      <SelectField
        label={t('consumptionsReport-form-commerceId-label')}
        name="commerceIds"
        placeholder={t('consumptionsReport-form-commerceId-placeholder')}
        value={formik.values.commerceIds}
        options={venueCommerces ? commercesForSelect(venueCommerces) : []}
        isMulti={true}
        setFieldValue={formik.setFieldValue}
        onBlur={formik.handleBlur}
        touched={formik.touched.commerceIds}
        error={formik.errors.commerceIds}
        isClearable={false}
        backspaceRemovesValue={false}
        isDisabled={formik.isSubmitting}
        isSearchable={true}
      />

      <TextField
        margin="dense"
        fullWidth
        id="identificationNumber"
        name="identificationNumber"
        label={t('consumptionsReport-page-identificationNumber')}
        value={formik.values.identificationNumber}
        onChange={formik.handleChange}
        error={formik.touched.identificationNumber && Boolean(formik.errors.identificationNumber)}
        helperText={formik.touched.identificationNumber && formik.errors.identificationNumber}
        disabled={formik.isSubmitting}
      />

      {state.length > 0 && state[0]?.entityId !== initialStateForCloverPoints[0]?.entityId && (
        <SelectField
          label={t('consumptionsReport-form-clover-points-label')}
          name="cloverPointIds"
          placeholder={t('consumptionsReport-form-clover-points-placeholder')}
          value={formik.values.cloverPointIds}
          options={state ? cloverPointsForSelect(state) : []}
          isMulti={true}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.cloverPointIds}
          error={formik.errors.cloverPointIds}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={true}
        />
      )}

      {!isForCashBoxes && (
        <div className="vertical-bottom-space">
          <FormControlLabel
            id="isCashPayment"
            name="isCashPayment"
            control={<Switch checked={formik.values.isCashPayment} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-clover-point-cash-order')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isCardPayment"
            name="isCardPayment"
            control={<Switch checked={formik.values.isCardPayment} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-clover-point-card-order')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isQrPayment"
            name="isQrPayment"
            control={<Switch checked={formik.values.isQrPayment} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-clover-point-fiserv-qr-order')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isFromCheckingAccount"
            name="isFromCheckingAccount"
            control={<Switch checked={formik.values.isFromCheckingAccount} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-clover-point-checking-account-order')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isPreOrder"
            name="isPreOrder"
            control={<Switch checked={formik.values.isPreOrder} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-clover-point-pre-order-order')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isForTransfer"
            name="isForTransfer"
            control={<Switch checked={formik.values.isForTransfer} onChange={formik.handleChange} />}
            label={t('consumptionsReport-form-transfer-order')}
            disabled={formik.isSubmitting}
          />
        </div>
      )}

      <TextField
        margin="dense"
        fullWidth
        name="dateFrom"
        label={t('consumptionsReport-form-dateFrom')}
        value={
          formik.values.dateFrom
            ? parseDateToFormik(moment(formik.values.dateFrom).toDate(), '')
            : formik.values.dateFrom
        }
        onChange={(event) => {
          formik.setFieldValue('dateFrom', moment(moment(event.target.value).tz(selectedVenueTimeZone ?? '')).toDate());
        }}
        error={formik.touched.dateFrom && Boolean(formik.errors.dateFrom)}
        helperText={formik.touched.dateFrom && formik.errors.dateFrom}
        disabled={formik.isSubmitting}
        type="datetime-local"
        InputLabelProps={{
          shrink: true,
        }}
      />
      {formik.values.dateFrom && (
        <TextField
          margin="dense"
          fullWidth
          name="dateTo"
          label={t('consumptionsReport-form-dateTo')}
          value={
            formik.values.dateTo ? parseDateToFormik(moment(formik.values.dateTo).toDate(), '') : formik.values.dateTo
          }
          onChange={(event) => {
            formik.setFieldValue('dateTo', moment(moment(event.target.value).tz(selectedVenueTimeZone ?? '')).toDate());
          }}
          error={formik.touched.dateTo && Boolean(formik.errors.dateTo)}
          helperText={formik.touched.dateTo && formik.errors.dateTo}
          disabled={formik.isSubmitting}
          type="datetime-local"
          InputLabelProps={{
            shrink: true,
          }}
        />
      )}

      {isForBillingOrders && (
        <div>
          <FormControlLabel
            id="onlyInvoicedOrders"
            name="onlyInvoicedOrders"
            control={<Switch checked={formik.values.onlyInvoicedOrders} onChange={formik.handleChange} />}
            label={t('order-billing-only-invoiced')}
            disabled={formik.isSubmitting}
          />
        </div>
      )}

      {justForDownload ? (
        <Box marginTop="1rem">
          <Button
            className="btn-50"
            variant="contained"
            color="primary"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || isLoading}
            onClick={() => {
              downloadReport();
            }}
          >
            {t('consumptionsReport-page-download')}
          </Button>
        </Box>
      ) : (
        <Box marginTop="1rem">
          <Button
            className="btn-50"
            variant="contained"
            color="primary"
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || isLoading}
          >
            {t('consumptionsReport-form-search')}
          </Button>
        </Box>
      )}
    </form>
  );
};
